import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Eye, Star, TrendingUp } from 'lucide-react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/images';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      if (response.data.success) {
        setProducts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = async (id, hard = false) => {
    const message = hard 
      ? 'Are you sure you want to PERMANENTLY delete this product? This cannot be undone!' 
      : 'Are you sure you want to delete this product? (Soft delete)';
    
    if (!confirm(message)) return;

    try {
      const endpoint = hard ? `/products/admin/${id}/hard` : `/products/admin/${id}`;
      await api.delete(endpoint);
      fetchProducts();
      alert(`Product ${hard ? 'permanently' : ''} deleted successfully!`);
    } catch (error) {
      alert('Error deleting product: ' + error.response?.data?.message);
    }
  };

  const handleToggleStock = async (id) => {
    try {
      await api.patch(`/products/admin/${id}/toggle-stock`);
      fetchProducts();
    } catch (error) {
      alert('Error updating stock status: ' + error.response?.data?.message);
    }
  };

  const handleToggleTrending = async (id) => {
    try {
      await api.patch(`/products/admin/${id}/toggle-trending`);
      fetchProducts();
    } catch (error) {
      alert('Error updating product: ' + error.response?.data?.message);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-5xl font-black tracking-tighter mb-2">PRODUCTS</h1>
          <div className="inline-block bg-yellow-400 text-black px-4 py-1 font-mono font-bold text-xs border-2 border-black">
            INVENTORY_MANAGEMENT
          </div>
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-black border-4 border-yellow-400 shadow-[8px_8px_0px_rgba(255,255,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(255,255,0,1)] transition-all"
        >
          <Plus size={20} />
          ADD NEW PRODUCT
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_rgba(0,0,0,1)] mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Search Products</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-10 pr-4 py-3 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Filter by Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-bold text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            >
              <option value="all">All Categories</option>
              <option value="anime">OTAKU</option>
              <option value="tantra">TANTRA</option>
              <option value="inkspeak">INKSPEAK</option>
              <option value="soul">SOUL VENTURE</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-20">
          <div className="text-2xl font-black animate-pulse">LOADING_PRODUCTS...</div>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white border-4 border-black p-12 text-center">
          <div className="text-gray-400 font-black text-2xl mb-4">NO PRODUCTS FOUND</div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-yellow-400 text-black px-6 py-3 font-black border-2 border-black hover:bg-yellow-300"
          >
            CREATE YOUR FIRST PRODUCT
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <div
              key={product._id}
              className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all"
            >
              {/* Product Image */}
              <div className="h-48 bg-gray-100 border-b-4 border-black flex items-center justify-center relative overflow-hidden">
                {product.images?.front?.url ? (
                  <img 
                    src={resolveImageUrl(product.images.front.url)} 
                    alt={product.name} 
                    className="w-full h-full object-cover" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : product.images?.streetwear?.[0]?.url ? (
                  <img 
                    src={resolveImageUrl(product.images.streetwear[0].url)} 
                    alt={product.name} 
                    className="w-full h-full object-cover" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="text-6xl font-black text-gray-300">{product.name.charAt(0)}</div>
                )}
                
                {/* Tags */}
                {product.tags && product.tags.length > 0 && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-black px-2 py-1 border-2 border-black">
                    {product.tags[0]}
                  </div>
                )}
                {product.isTrending && (
                  <div className="absolute top-2 right-2 bg-yellow-400 text-black p-2 border-2 border-black">
                    <TrendingUp size={16} />
                  </div>
                )}
                {!product.inStock && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <span className="text-white font-black text-xl bg-red-600 px-4 py-2 border-2 border-white">OUT OF STOCK</span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="text-xs font-bold uppercase bg-gray-100 px-2 py-1 border border-gray-300">
                    {product.category}
                  </div>
                  <div className="text-xs font-bold uppercase text-gray-600">
                    {product.type}
                  </div>
                </div>

                {product.dropName && (
                  <div className="mb-2">
                    <span className="inline-block text-xs font-black uppercase bg-yellow-400 text-black px-2 py-1 border border-black">
                      📦 {product.dropName}
                    </span>
                  </div>
                )}

                <h3 className="font-black text-lg mb-2 truncate">{product.name}</h3>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="text-2xl font-black">${product.price}</div>
                  <div className="text-sm font-bold text-gray-600">
                    Stock: <span className={product.totalStock < 10 ? 'text-red-600' : 'text-green-600'}>{product.totalStock}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button
                    onClick={() => handleToggleTrending(product._id)}
                    className={`p-2 border-2 border-black font-bold text-xs transition-colors ${
                      product.isTrending ? 'bg-yellow-400 text-black' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                    title="Toggle Trending"
                  >
                    <TrendingUp size={16} className="mx-auto" />
                  </button>
                  <button
                    onClick={() => handleToggleStock(product._id)}
                    className={`p-2 border-2 border-black font-bold text-xs transition-colors ${
                      product.inStock ? 'bg-green-400 text-black' : 'bg-red-400 text-white'
                    }`}
                    title="Toggle Stock Status"
                  >
                    {product.inStock ? 'IN STOCK' : 'OUT'}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setEditingProduct(product);
                      setShowModal(true);
                    }}
                    className="p-2 bg-blue-500 text-white border-2 border-black hover:bg-blue-600"
                    title="Edit"
                  >
                    <Edit size={16} className="mx-auto" />
                  </button>
                  <button
                    onClick={() => handleDelete(product._id, false)}
                    className="p-2 bg-orange-500 text-white border-2 border-black hover:bg-orange-600"
                    title="Soft Delete"
                  >
                    <Trash2 size={16} className="mx-auto" />
                  </button>
                  <button
                    onClick={() => handleDelete(product._id, true)}
                    className="p-2 bg-red-600 text-white border-2 border-black hover:bg-red-700"
                    title="Hard Delete (Permanent)"
                  >
                    <Trash2 size={16} className="mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Modal (Add/Edit) */}
      {showModal && (
        <ProductModal
          product={editingProduct}
          onClose={() => {
            setShowModal(false);
            setEditingProduct(null);
          }}
          onSuccess={() => {
            fetchProducts();
            setShowModal(false);
            setEditingProduct(null);
          }}
        />
      )}
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ product, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'anime',
    type: product?.type || 'Normal',
    dropName: product?.dropName || '',
    price: product?.price || '',
    sizes: product?.sizes || [{ size: 'M', stock: 0 }],
    tags: product?.tags || [],
    isTrending: product?.isTrending || false,
    isFeatured: product?.isFeatured || false,
    inStock: product?.inStock !== undefined ? product.inStock : true
  });
  const [images, setImages] = useState({
    front: null,
    back: null,
    closeup: null,
    folded: null,
    streetwear: []
  });
  const [existingDrops, setExistingDrops] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchExistingDrops();
  }, []);

  const fetchExistingDrops = async () => {
    try {
      const response = await api.get('/products/admin/drops');
      if (response.data.success) {
        setExistingDrops(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching drops:', error);
    }
  };

  const handleImageChange = (type, files) => {
    if (type === 'streetwear') {
      setImages(prev => ({ ...prev, streetwear: Array.from(files) }));
    } else {
      setImages(prev => ({ ...prev, [type]: files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Append regular form data
      Object.keys(formData).forEach(key => {
        if (key === 'sizes' || key === 'tags') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append image files
      if (images.front) formDataToSend.append('front', images.front);
      if (images.back) formDataToSend.append('back', images.back);
      if (images.closeup) formDataToSend.append('closeup', images.closeup);
      if (images.folded) formDataToSend.append('folded', images.folded);
      images.streetwear.forEach(file => {
        formDataToSend.append('streetwear', file);
      });

      if (product) {
        await api.put(`/products/admin/${product._id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/products/admin/create', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      alert(`Product ${product ? 'updated' : 'created'} successfully!`);
      onSuccess();
    } catch (error) {
      alert('Error: ' + error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border-4 border-yellow-400 shadow-[16px_16px_0px_rgba(255,255,0,1)] w-full max-w-2xl my-8">
        <div className="bg-yellow-400 p-4 border-b-4 border-black flex justify-between items-center">
          <h2 className="text-2xl font-black">{product ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}</h2>
          <button onClick={onClose} className="text-2xl font-black hover:text-red-600">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Product Name */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Product Name*</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold"
            />
          </div>

          {/* Category & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase mb-2">Category*</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-3 border-2 border-black font-bold bg-white"
              >
                <option value="anime">OTAKU</option>
                <option value="tantra">TANTRA</option>
                <option value="inkspeak">INKSPEAK</option>
                <option value="soul">SOUL VENTURE</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-black uppercase mb-2">Type*</label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-3 border-2 border-black font-bold bg-white"
              >
                <option value="Normal">Normal</option>
                <option value="Drop 01">Drop 01</option>
                <option value="Essentials">Essentials</option>
                <option value="Accessories">Accessories</option>
                <option value="Flash Sale">Flash Sale</option>
              </select>
            </div>
          </div>

          {/* Drop Name */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Drop Name (Optional)</label>
            <input
              type="text"
              list="existing-drops"
              value={formData.dropName}
              onChange={(e) => setFormData({...formData, dropName: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold"
              placeholder="Type new drop name or select existing..."
            />
            <datalist id="existing-drops">
              {existingDrops.map((drop, index) => (
                <option key={index} value={drop} />
              ))}
            </datalist>
            <p className="text-xs text-gray-600 mt-1 font-mono">
              💡 Select from existing drops or type a new name to create one
            </p>
          </div>

          {/* Price */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Price (₹)*</label>
            <input
              type="number"
              required
              min="0"
              step="1"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold"
              placeholder="999"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Description*</label>
            <textarea
              required
              rows="4"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-mono text-sm"
            />
          </div>

          {/* Size & Stock Management */}
          <div className="border-4 border-black p-4 bg-blue-50">
            <h3 className="text-lg font-black mb-4 uppercase">Size & Stock Inventory</h3>
            
            <div className="space-y-3">
              {formData.sizes.map((sizeItem, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <select
                    value={sizeItem.size}
                    onChange={(e) => {
                      const newSizes = [...formData.sizes];
                      newSizes[index].size = e.target.value;
                      setFormData({...formData, sizes: newSizes});
                    }}
                    className="px-3 py-2 border-2 border-black font-bold bg-white"
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="XXXL">XXXL</option>
                  </select>
                  
                  <input
                    type="number"
                    min="0"
                    placeholder="Stock"
                    value={sizeItem.stock}
                    onChange={(e) => {
                      const newSizes = [...formData.sizes];
                      newSizes[index].stock = parseInt(e.target.value) || 0;
                      setFormData({...formData, sizes: newSizes});
                    }}
                    className="flex-1 px-3 py-2 border-2 border-black font-mono"
                  />
                  
                  <button
                    type="button"
                    onClick={() => {
                      const newSizes = formData.sizes.filter((_, i) => i !== index);
                      setFormData({...formData, sizes: newSizes});
                    }}
                    className="px-3 py-2 bg-red-500 text-white border-2 border-black font-bold"
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    ...formData,
                    sizes: [...formData.sizes, { size: 'M', stock: 0 }]
                  });
                }}
                className="w-full px-4 py-2 bg-green-400 border-2 border-black font-bold hover:bg-green-300"
              >
                + ADD SIZE
              </button>
            </div>
          </div>

          {/* Image Uploads */}
          <div className="border-4 border-black p-4 bg-yellow-50">
            <h3 className="text-lg font-black mb-4 uppercase">Product Images</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-black uppercase mb-2">Front Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange('front', e.target.files)}
                  className="w-full text-sm border-2 border-black p-2"
                />
                {product?.images?.front?.url && !images.front && (
                  <div className="mt-2 p-2 border-2 border-gray-300">
                    <img 
                      src={resolveImageUrl(product.images.front.url)} 
                      alt="Current front" 
                      className="h-20 object-cover" 
                    />
                    <p className="text-xs text-gray-500">Current image</p>
                  </div>
                )}
                {images.front && (
                  <div className="mt-2 p-2 border-2 border-green-500">
                    <p className="text-xs text-green-600 font-bold">✓ New image selected</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-black uppercase mb-2">Back Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange('back', e.target.files)}
                  className="w-full text-sm border-2 border-black p-2"
                />
                {product?.images?.back?.url && !images.back && (
                  <div className="mt-2 p-2 border-2 border-gray-300">
                    <img 
                      src={resolveImageUrl(product.images.back.url)} 
                      alt="Current back" 
                      className="h-20 object-cover" 
                    />
                    <p className="text-xs text-gray-500">Current image</p>
                  </div>
                )}
                {images.back && (
                  <div className="mt-2 p-2 border-2 border-green-500">
                    <p className="text-xs text-green-600 font-bold">✓ New image selected</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-black uppercase mb-2">Closeup Design</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange('closeup', e.target.files)}
                  className="w-full text-sm border-2 border-black p-2"
                />
                {product?.images?.closeup?.url && !images.closeup && (
                  <div className="mt-2 p-2 border-2 border-gray-300">
                    <img 
                      src={resolveImageUrl(product.images.closeup.url)} 
                      alt="Current closeup" 
                      className="h-20 object-cover" 
                    />
                    <p className="text-xs text-gray-500">Current image</p>
                  </div>
                )}
                {images.closeup && (
                  <div className="mt-2 p-2 border-2 border-green-500">
                    <p className="text-xs text-green-600 font-bold">✓ New image selected</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-black uppercase mb-2">Folded Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange('folded', e.target.files)}
                  className="w-full text-sm border-2 border-black p-2"
                />
                {product?.images?.folded?.url && !images.folded && (
                  <div className="mt-2 p-2 border-2 border-gray-300">
                    <img 
                      src={resolveImageUrl(product.images.folded.url)} 
                      alt="Current folded" 
                      className="h-20 object-cover" 
                    />
                    <p className="text-xs text-gray-500">Current image</p>
                  </div>
                )}
                {images.folded && (
                  <div className="mt-2 p-2 border-2 border-green-500">
                    <p className="text-xs text-green-600 font-bold">✓ New image selected</p>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-black uppercase mb-2">Streetwear Photos (Multiple)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageChange('streetwear', e.target.files)}
                className="w-full text-sm border-2 border-black p-2"
              />
              <p className="text-xs text-gray-600 mt-1">You can select multiple streetwear photos at once (up to 10)</p>
              {product?.images?.streetwear?.length > 0 && images.streetwear.length === 0 && (
                <div className="mt-2 p-2 border-2 border-gray-300">
                  <p className="text-xs text-gray-500 font-bold">{product.images.streetwear.length} current streetwear photos</p>
                </div>
              )}
              {images.streetwear.length > 0 && (
                <div className="mt-2 p-2 border-2 border-green-500">
                  <p className="text-xs text-green-600 font-bold">✓ {images.streetwear.length} new photos selected</p>
                </div>
              )}
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 font-bold">
              <input
                type="checkbox"
                checked={formData.isTrending}
                onChange={(e) => setFormData({...formData, isTrending: e.target.checked})}
                className="w-5 h-5"
              />
              Trending Product
            </label>
            <label className="flex items-center gap-2 font-bold">
              <input
                type="checkbox"
                checked={formData.isFeatured}
                onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                className="w-5 h-5"
              />
              Featured Product
            </label>
            <label className="flex items-center gap-2 font-bold">
              <input
                type="checkbox"
                checked={formData.inStock}
                onChange={(e) => setFormData({...formData, inStock: e.target.checked})}
                className="w-5 h-5"
              />
              In Stock
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black text-lg uppercase py-4 border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
          >
            {loading ? 'SAVING...' : (product ? 'UPDATE PRODUCT' : 'CREATE PRODUCT')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductManagement;
